import type { ChangeEvent, ComponentProps, HTMLProps, ReactNode } from 'react';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useSelector } from 'react-redux';
import { IconAdd, IconUpload } from '@bynder/icons';
import { Button } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import { getUploadOptions } from '~/store/assets/assets.actions';
import useUploader from './useUploader';
import { StyledLabel } from './Component.styled';
import { ACCEPT, AssetType, UploaderState, UploadService } from './types';

type Props = {
    collectionId?: number;
    assetTypes?: AssetType[];
    iconType: 'add' | 'upload';
    disabled?: boolean;
    children?: ReactNode;
    buttonProps?: ComponentProps<typeof Button>;
    inputProps?: HTMLProps<HTMLInputElement>;
    uploadService?: UploadService;
    hideNotification?: boolean;
    onChange?: (file: File) => void;
    multiple?: boolean;
};

const selector = (state: UploaderState) => state.status !== 'ready';
const uploadOptionsSelector = (state: any): any => state.assets.uploadOptions;

export type UploadButtonHandle = {
    onClick: () => void;
};

const UploadButton = forwardRef<UploadButtonHandle, Props>(function UploadButton(
    {
        disabled = false,
        iconType,
        assetTypes = ['all'],
        collectionId,
        children,
        buttonProps,
        inputProps,
        uploadService,
        hideNotification,
        onChange,
        multiple = false,
    },
    ref,
) {
    const { translate } = useTranslate();
    const inputRef = useRef<HTMLInputElement>(null);
    const { upload, state: isLoading } = useUploader({ selector });
    const uploadOptions = useSelector(uploadOptionsSelector);
    const dispatch = useThunkDispatch();

    useEffect(() => {
        if (!uploadOptions) {
            dispatch(getUploadOptions());
        }
    }, []);

    useImperativeHandle(ref, () => {
        return {
            onClick() {
                inputRef.current?.click();
            },
        };
    }, []);

    const isDisabled = isLoading || disabled;

    const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
        if (isDisabled) {
            return;
        }

        const files = Array.from(event.target.files);

        if (!files.length) {
            return;
        }

        upload(files, assetTypes, collectionId, uploadService, hideNotification);
        inputRef.current!.value = '';

        if (onChange) {
            onChange(files[files.length - 1]);
        }
    };

    const label = translate(isLoading ? 'pages.uploader.button.uploading' : 'pages.uploader.button.upload');
    const icon = !isLoading && (iconType === 'upload' ? <IconUpload /> : <IconAdd />);

    if (!uploadOptions) {
        return null;
    }

    return (
        <StyledLabel>
            <Button
                variant="primary"
                isLoading={isLoading}
                isDisabled={isDisabled}
                onClick={() => inputRef.current?.click()}
                rightIcon={iconType === 'add' && icon}
                icon={iconType === 'upload' && icon}
                {...(buttonProps || {})}
            >
                {children || label}
            </Button>
            <input
                ref={inputRef}
                hidden
                disabled={isDisabled}
                type="file"
                multiple={multiple}
                onChange={handleUpload}
                accept={assetTypes.map((assetType) => ACCEPT[assetType]).join(', ')}
                {...(inputProps || {})}
            />
        </StyledLabel>
    );
});

export default UploadButton;
